<template>
  <ModalShell :visible="visible" :z-index="3000" padding="20px" @close="close">
    <div class="action_key_dialog_overlay">
      <ModalPanel class="action_key_dialog_card" width="90%" max-width="1000px" max-height="90vh" background="#ffffff" border="1px solid rgba(255, 255, 255, 0.1)" shadow="0 40px 100px rgba(0, 0, 0, 0.25)">
        <!-- Header -->
        <div class="dialog_header">
          <div class="header_title_box">
            <div class="title_tag">{{ currentTitleTag }}</div>
            <div class="title_text">{{ t('config_output_action') }}</div>
          </div>
          <div class="header_actions">
            <div class="mgx_action_btn primary confirm_btn" @click="confirm">
              <div class="icon-wrapper">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round">
                  <polyline points="20 6 9 17 4 12" />
                </svg>
              </div>
              <span>{{ t('confirm') }}</span>
            </div>
            <div class="mgx_close_btn" @click="close">
              <svg viewBox="0 0 24 24" fill="none">
                <path d="M18 6L6 18M6 6l12 12" stroke-linecap="round" stroke-linejoin="round" />
              </svg>
            </div>
          </div>
        </div>

        <div class="dialog_body">
          <div class="grid_tabs">
            <div class="grid_tab" :class="{ active: actionSet.actionType.value == ActionType.base && actionSet.baseKeys.baseTab == BaseTabs.base }" @click="changeGridTab(ActionType.base, BaseTabs.base)">
              {{ $t('action_set_base_chat') }}
            </div>
            <div class="grid_tab" :class="{ active: actionSet.actionType.value == ActionType.base && actionSet.baseKeys.baseTab == BaseTabs.extend }" @click="changeGridTab(ActionType.base, BaseTabs.extend)">
              {{ $t('action_set_special_chat') }}
            </div>
            <div v-if="isSupportCompose" class="grid_tab" :class="{ active: actionSet.actionType.value == ActionType.base && actionSet.baseKeys.baseTab == BaseTabs.compose }" @click="changeGridTab(ActionType.base, BaseTabs.compose)">
              {{ $t('action_set_combination_key') }}
            </div>
            <div v-if="isSupportMouse" class="grid_tab" :class="{ active: actionSet.actionType.value == ActionType.base && actionSet.baseKeys.baseTab == BaseTabs.mouse }" @click="changeGridTab(ActionType.base, BaseTabs.mouse)">
              {{ $t('action_set_mouse_key') }}
            </div>
            <div v-if="isSupportMultiMedia" class="grid_tab" :class="{ active: actionSet.actionType.value == ActionType.multiMedia }" @click="changeGridTab(ActionType.multiMedia)">
              {{ $t('action_set_multimedia') }}
            </div>
          </div>

          <div class="grid_content_wrapper">
            <div class="grid_content scrollbar_thumb">
              <base_keys v-if="actionSet.actionType.value == ActionType.base" :tab="actionSet.baseKeys.baseTab" :set-type="actionSet.setType.value" />
              <multimedia v-if="actionSet.actionType.value == ActionType.multiMedia" />
            </div>
          </div>
        </div>
      </ModalPanel>
    </div>
  </ModalShell>
</template>

<script lang="ts" setup>
import { actionSet, ActionType, BaseTabs, SetType } from '@/service/driver/action_set'
import { computed, watch } from 'vue'
import ModalShell from '@/components/common/ModalShell.vue'
import ModalPanel from '@/components/common/ModalPanel.vue'
import base_keys from './base_keys.vue'
import multimedia from './multimedia.vue'
import i18n from '@/languages/i18n'
import { checkSupport } from './action_config'
import { BaseKeys, SpecialKeys, mouseBaseKeys, mediaKeys } from '@/define/vkcode' // 导入数据检索函数

const { t } = i18n.global

const props = defineProps<{
  visible: boolean
  rowIndex: number
  baseTab: BaseTabs
  selectedKeys: number[]
}>()

const emit = defineEmits(['update:visible', 'confirm'])

const isSupportCompose = computed(() => checkSupport.compose())
const isSupportMouse = computed(() => checkSupport.mouse())
const isSupportMultiMedia = computed(() => checkSupport.multiMedia())

const currentTitleTag = computed(() => {
  const type = actionSet.setType.value
  switch (type) {
    case SetType.dks:
      return `DKS ${t('dks_slot_prefix')}${props.rowIndex + 1}`
    case SetType.mt:
      return `MT ${props.rowIndex === 0 ? t('action_set_click_trigger') : t('action_set_press_trigger')}`
    case SetType.tgl:
      return 'TGL'
    case SetType.kme:
      return 'KME'
    case SetType.rs:
      return 'RS'
    case SetType.mpt:
      return `MPT ${t('dks_slot_prefix')}${props.rowIndex + 1}`
    case SetType.end:
      return 'END'
    default:
      return ''
  }
})
watch(
  () => props.visible,
  (val) => {
    if (val) {
      // 1. 同步初期选中的按键数据
      if (props.selectedKeys) {
        actionSet.changeSelectedKeys(props.selectedKeys)

        const mainKey = props.selectedKeys[2]

        // 2. 动态纠偏 Tab 状态 (核心：确保从池子中动态匹配)
        if (mainKey > 0) {
          if (mediaKeys().includes(mainKey)) {
            changeGridTab(ActionType.multiMedia)
          } else {
            changeGridTab(ActionType.base)
            if (BaseKeys().includes(mainKey)) {
              actionSet.changeBaseTab(BaseTabs.base)
            } else if (SpecialKeys().includes(mainKey)) {
              actionSet.changeBaseTab(BaseTabs.extend)
            } else if (mouseBaseKeys().includes(mainKey) || mainKey === 0xfa) {
              actionSet.changeBaseTab(BaseTabs.mouse)
            }
          }
        } else if (props.baseTab !== undefined) {
          // 如果是空的（如设置组合键），则跟随父组件请求的 Tab
          // 同时强制将 actionType 重置为 base，避免因上次选中的 deviceFunction 导致内容区空白
          changeGridTab(ActionType.base, props.baseTab)
        }

        // 核心修复：通过异步微调，强制触发 Vue 响应式系统重新对 base_keys 进行高亮计算
        setTimeout(() => {
          if (props.selectedKeys) {
            actionSet.changeSelectedKeys([...props.selectedKeys])
          }
        }, 50)
      }

      // 3. 兜底安全性检查
      if ((actionSet.baseKeys.baseTab == BaseTabs.mouse && !checkSupport.mouse()) || (actionSet.actionType.value == ActionType.multiMedia && !checkSupport.multiMedia())) {
        changeGridTab(ActionType.base, BaseTabs.base)
      }
    }
  },
)
const close = () => {
  emit('update:visible', false)
}

const confirm = () => {
  emit('confirm')
  close()
}

const changeGridTab = (type: ActionType, tab: BaseTabs = BaseTabs.base) => {
  if (actionSet.actionType.value !== type) {
    actionSet.changeActionType(type)
  }
  if (tab !== undefined) {
    actionSet.changeBaseTab(tab)
  }
}
</script>

<style lang="less" scoped>
.action_key_dialog_overlay {
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100%;
  min-height: 100%;
  overflow-y: auto;
}

.action_key_dialog_card {
  border-radius: var(--radius-l);
  animation: modal-enter 0.4s cubic-bezier(0.23, 1, 0.32, 1);
}

@keyframes modal-enter {
  from {
    opacity: 0;
    transform: translateY(20px) scale(0.98);
  }
  to {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
}

.dialog_header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 24px 32px;
  border-bottom: 1px solid rgba(0, 0, 0, 0.05);

  .header_title_box {
    display: flex;
    align-items: center;
    gap: 12px;

    .title_tag {
      background: var(--brand-color);
      color: #fff;
      font-size: 12px;
      font-weight: 800;
      padding: 4px 10px;
      border-radius: var(--action-btn-radius);
      text-transform: uppercase;
    }

    .title_text {
      font-size: 18px;
      font-weight: 700;
      color: var(--color-dark);
    }
  }

  .header_actions {
    display: flex;
    align-items: center;
    gap: 16px;

    .confirm_btn {
      /* 继承全局 .mgx_action_btn primary 样式 */
      font-weight: 700;
    }
  }
}

.dialog_body {
  padding: 24px 32px;
  display: flex;
  flex-direction: column;
  gap: 20px;
  flex: 1;
  min-height: 400px;
  overflow-y: auto;

  .grid_tabs {
    display: flex;
    gap: 24px;
    // border-bottom: 1px solid rgba(0, 0, 0, 0.05);
    overflow-x: auto;
    white-space: nowrap;
    scrollbar-width: none; // Hide scrollbar for cleaner look

    &::-webkit-scrollbar {
      display: none;
    }

    .grid_tab {
      padding: 12px 16px;
      font-size: 15px;
      font-weight: 500;
      color: var(--color-text-hint);
      cursor: pointer;
      position: relative;
      transition: var(--transition-base);

      &::after {
        content: '';
        position: absolute;
        bottom: -1px;
        left: 0;
        width: 100%;
        height: 3px;
        background: var(--brand-color-pure);
        border-radius: 100px;
        transform: scaleX(0);
        transition: var(--transition-base);
      }

      &.active {
        color: var(--brand-color-pure);
        font-weight: 700;
        &::after {
          transform: scaleX(1);
        }
      }
    }
  }

  .grid_content_wrapper {
    flex: 1;
    background: var(--color-gray-1);
    border-radius: var(--radius-l);
    padding: 16px;
    display: flex;
    flex-direction: column;
    overflow: hidden;
    contain: layout paint;

    .grid_content {
      flex: 1;
      overflow-y: auto;
      overscroll-behavior: contain;
    }
  }
}
</style>
